'use strict';

import * as domUtils from '../util/dom';
import videoAttributes from '../util/video-attributes';
import Sizzle from 'sizzle';

const NAMESPACE = 'metrics';
const UPDATE_METRICS_INTERVAL = 1000; //ms
const HIDE_CONTEXT_MENU_AFTER = 7000; //ms

const instances = new Map();

class Metrics {
    constructor(api, uid) {
        this.api = api;
        this.enable = false;
        this.interval = null;
        this.id = 'svp-metrics-' + uid;
        this.menuId = 'svp-metrics-menu-' + uid;
        this.contextMenuVisible = false;
        this.markupAdded = false;
        this.enableContextMenu = false;
        this.hideContextMenuTimer = null;

        this._addListeners();
    }

    _createMarkup() {
        return domUtils.fromString(
            '<div class="svp_metrics svp_css-container--font" id="' + this.id + '">\
                <p class="svp_metrics__row">\
                    <span id="' + this.id + '-abs">&nbsp;</span>\
                </p>\
                <p class="svp_metrics__row">Dropped frames: \
                    <span id="' + this.id + '-dropped"></span>\
                </p>\
                <p class="svp_metrics__row">Buffer level: \
                    <span id="' + this.id + '-buffer"></span>\
                </p>\
                <p class="svp_metrics__row">CDN: \
                    <span id="' + this.id + '-cdn"></span>\
                </p>\
                <p class="svp_metrics__row">Dimensions: \
                    <span id="' + this.id + '-dimensions"></span>\
                </p>\
                <p class="svp_metrics__row">Resolution: \
                    <span id="' + this.id + '-resolution"></span>\
                </p>\
            </div>');
    }

    _createMenuMarkup(x, y) {
        const showOrHide = this.enable ? 'Dölj' : 'Visa';

        return domUtils.fromString(
            '<div class="svp_metrics-menu" id="' + this.menuId + '" style="left: ' + x + 'px; top: ' + y + 'px;">\
                <ul class="svp_metrics-menu-list">\
                    <li class="svp-metrics-menu__item">\
                        <a class="svp-metrics-menu__item-button svp_js-show-debug-info" role="button">' + showOrHide + ' mer videoinformation</a>\
                    </li>\
                </ul>\
            </ul>');
    }

    _keyDownListener(e) {
        if (e.key === 'd') {
            this.enable = !this.enable;

            if (this.enable) {
                this.start();
            } else {
                this.stop(true);
            }
        }
    }

    _cumulativeOffset(element) {
        let top = 0;
        let left = 0;

        do {
            top += element.offsetTop || 0;
            left += element.offsetLeft || 0;
            element = element.offsetParent;
        } while (element);

        return {
            top: top,
            left: left
        };
    }

    _showContextMenu(e) {
        if (this.contextMenuVisible) {
            return this._hideContextMenu();
        }
        if (!this.enableContextMenu) {
            return;
        }

        e.preventDefault();

        const offset = this._cumulativeOffset(e.target);

        this.api.element().appendChild(
            this._createMenuMarkup(e.pageX - offset.left, e.pageY - offset.top)
        );

        const debugButton = Sizzle('.svp_js-show-debug-info', this.api.element())[0];

        debugButton.addEventListener('click', this._showDebugButtonClick.bind(this));

        this.contextMenuVisible = true;

        this.hideContextMenuTimer = setTimeout(() => {
            this._hideContextMenu();
        }, HIDE_CONTEXT_MENU_AFTER);
    }

    _showDebugButtonClick(e) {
        e.preventDefault();

        const debugButton = Sizzle('.svp_js-show-debug-info', this.api.element())[0];

        if (debugButton) {
            debugButton.removeEventListener('click', this._showDebugButtonClick.bind(this));
        }

        this.enable = !this.enable;

        if (this.enable) {
            this.start();
        } else {
            this.stop(true);
        }

        this._hideContextMenu();
    }

    _hideContextMenu() {
        const markuptoRemove = document.getElementById(this.menuId);

        if (markuptoRemove) {
            this.api.element().removeChild(markuptoRemove);
        }

        if (this.hideContextMenuTimer) {
            clearTimeout(this.hideContextMenuTimer);
            this.hideContextMenuTimer = null;
        }

        this.contextMenuVisible = false;
    }

    _addListeners() {

        const element = this.api.element();
        domUtils.addEvents(element, 'keydown', this._keyDownListener.bind(this), NAMESPACE);
        domUtils.addEvents(element, 'contextmenu', this._showContextMenu.bind(this), NAMESPACE);
        domUtils.addEvents(element, 'click', this._hideContextMenu.bind(this), NAMESPACE);

        this.api.on(NAMESPACE + '.playing', () => {
            this.enableContextMenu = true;

            if (this.enable && !this.playing) {
                this.start();
            }

            this.playing = true;
        });

        this.api.on(NAMESPACE + '.pause', () => {
            this.playing = false;

            if (this.enable) {
                this.stop();
            }
        });

        this.api.on(NAMESPACE + '.resize', () => {
            this._hideContextMenu();
        });
    }

    _removeListerners() {
        domUtils.removeEvents(this.api.element(), 'keydown, contextmenu, click', NAMESPACE);
        this.api.off(NAMESPACE + '.playing');
        this.api.off(NAMESPACE + '.pause');
    }

    start() {
        if (!this.markupAdded) {
            this.api.element().appendChild(this._createMarkup());
            this.markupAdded = true;
        }

        this._startMetricsInterval();
    }

    stop(removeMarkup) {
        this._clearMetricsInterval();

        if (removeMarkup) {
            const markuptoRemove = document.getElementById(this.id);

            if (markuptoRemove) {
                this.api.element().removeChild(markuptoRemove);
                this.markupAdded = false;
            }
        }
    }

    _startMetricsInterval() {
        this.interval = setInterval(() => {
            const metrics = this.api.getMetrics();

            document.getElementById(this.id + '-abs').innerHTML = metrics.dash ? 'MPEG-DASH' : 'HLS';
            document.getElementById(this.id + '-dropped').innerHTML = metrics.droppedFrames;
            document.getElementById(this.id + '-buffer').innerHTML = Math.floor(metrics.bufferLevel) + 's';
            document.getElementById(this.id + '-cdn').innerHTML = metrics.cdn || 'N/A';
            document.getElementById(this.id + '-dimensions').innerHTML = metrics.dimensions;
            document.getElementById(this.id + '-resolution').innerHTML = metrics.resolution;
        }, UPDATE_METRICS_INTERVAL);
    }

    _clearMetricsInterval() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    }

    destroy() {
        this.stop(true);
        this._removeListerners();
    }
}

module.exports = {
    create: function (api) {
        api.once(NAMESPACE + '.ready', () => {
            const videoElement = api.video();
            const uid = videoAttributes.getUID(videoElement) || videoAttributes.generateAndSetUID(videoElement);

            const metrics = new Metrics(api, uid);
            instances.set(api, metrics);
        });
    },
    destroy: function (api) {
        if (instances.has(api)) {
            instances.get(api).destroy();
            instances.delete(api);
        }
    }
};




// WEBPACK FOOTER //
// ./src/js/plugins/metrics.js